import { ref } from 'vue'

import { transformNotificationOpts, useHLNotification } from '@platform-ui/highrise'

import { Z_INDEX } from '../constants'
import { AlertType, NOTIFICATION_DURATIONS } from '../constants/constants'

export interface NotificationOptions {
  title: string
  description?: string
  content?: string
  meta?: string
  duration?: number
  /** Whether to show a close button on the toast. Defaults to the underlying component default. */
  closable?: boolean
  placement?: 'top' | 'top-right' | 'bottom' | 'bottom-right'
}

export interface NotificationRef {
  destroy: () => void
}

/**
 * Generic notification utility for consistent notifications across the app
 */
export const useNotificationHelper = () => {
  const notification = useHLNotification()
  const notifRef = ref<NotificationRef | null>(null)

  /**
   * Show a success notification
   */
  const showSuccess = (options: NotificationOptions): NotificationRef => {
    const oldOpts = {
      title: options.title,
      duration: options.duration || NOTIFICATION_DURATIONS.DEFAULT,
      type: AlertType.SUCCESS,
      description: options.description || '',
      content: options.content || '',
      meta: options.meta || '',
      ...(options.closable !== undefined ? { closable: options.closable } : {}),
    }

    const instance = notification.create({
      ...transformNotificationOpts(oldOpts),
      placement: options.placement || 'top',
      zIndex: Z_INDEX.TOAST,
    })
    notifRef.value = instance
    return instance
  }

  /**
   * Show an error notification
   */
  const showError = (options: NotificationOptions): NotificationRef => {
    const oldOpts = {
      title: options.title,
      duration: options.duration || NOTIFICATION_DURATIONS.LONG,
      type: AlertType.ERROR,
      description: options.description || '',
      content: options.content || '',
      meta: options.meta || '',
      ...(options.closable !== undefined ? { closable: options.closable } : {}),
    }

    const instance = notification?.create({
      ...transformNotificationOpts(oldOpts),
      placement: options.placement || 'top',
      zIndex: Z_INDEX.TOAST,
    })
    notifRef.value = instance
    return instance
  }

  /**
   * Show a warning notification
   */
  const showWarning = (options: NotificationOptions): NotificationRef => {
    const oldOpts = {
      title: options.title,
      duration: options.duration || NOTIFICATION_DURATIONS.DEFAULT,
      type: AlertType.WARNING,
      description: options.description || '',
      content: options.content || '',
      meta: options.meta || '',
      ...(options.closable !== undefined ? { closable: options.closable } : {}),
    }

    const instance = notification.create({
      ...transformNotificationOpts(oldOpts),
      placement: options.placement || 'top',
      zIndex: Z_INDEX.TOAST,
    })
    notifRef.value = instance
    return instance
  }

  /**
   * Show an info notification
   */
  const showInfo = (options: NotificationOptions): NotificationRef => {
    const oldOpts = {
      title: options.title,
      duration: options.duration || NOTIFICATION_DURATIONS.DEFAULT,
      type: AlertType.INFO,
      description: options.description || '',
      content: options.content || '',
      meta: options.meta || '',
      ...(options.closable !== undefined ? { closable: options.closable } : {}),
    }

    const instance = notification.create({
      ...transformNotificationOpts(oldOpts),
      placement: options.placement || 'top',
      zIndex: Z_INDEX.TOAST,
    })
    notifRef.value = instance
    return instance
  }

  /**
   * Destroy the current notification
   */
  const destroy = () => {
    if (notifRef.value) {
      notifRef.value.destroy()
      notifRef.value = null
    }
  }

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    destroy,
    notifRef,
  }
}
